import React from 'react';

import { Link } from 'gatsby';
import styled from 'styled-components';
import { ButtonColour } from './components/Button';
import { ButtonLink } from './components/ButtonLink';
import { useCookieSettings } from './context/CookieSettingsContext';
import { PageDocumentType } from './types/types';
import { getPageDocumentUrl } from './utils/sanity';

const StyledAsLink = styled.a`
  transition: all 0.2s ease-in-out;
  padding: 0 2px;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    background-color: var(--tts-blue);
    color: white;
    text-decoration: none;
  }

  &:focus {
    background-color: var(--tts-blue);
    color: white;
    text-decoration: underline;
    text-decoration-color: var(--tts-black);
    outline: none;
  }
`;

export const InternalLinkSerializer = (props: {
  mark: {
    linkToCookieSettings?: boolean;
    reference: {
      slug: { current: string };
      _type: PageDocumentType;
    };
  };
  children: React.ReactNode;
}): React.ReactElement => {
  const { showCookieSettingsModal } = useCookieSettings();

  if (props.mark.linkToCookieSettings) {
    return (
      <StyledAsLink
        onClick={() => {
          showCookieSettingsModal();
        }}
      >
        {props.children}
      </StyledAsLink>
    );
  } else {
    return <Link to={getPageDocumentUrl(props.mark.reference)}>{props.children}</Link>;
  }
};

const LinkSerializer = (props: {
  mark: {
    href?: string;
    file?: {
      asset: {
        url: string;
      };
    };
  };
  children: React.ReactNode;
}): React.ReactElement => {
  return (
    <a
      href={props.mark.href || props.mark.file?.asset.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
    </a>
  );
};

const ButtonSerializer = (props: {
  node: {
    pageReference?: {
      slug: { current: string };
      _type: PageDocumentType;
    };
    url?: string;
    backgroundColour: ButtonColour;
    text: string;
  };
  children: React.ReactNode;
}): React.ReactElement => {
  const destination = props.node.pageReference
    ? getPageDocumentUrl(props.node.pageReference)
    : props.node.url;
  if (destination) {
    return (
      <ButtonLink backgroundColour={props.node.backgroundColour} to={destination}>
        {props.node.text}
      </ButtonLink>
    );
  }
  return <></>;
};

const HorizontalLineSerializer = (): React.ReactElement => {
  return (
    <hr
      style={{
        margin: 'var(--styled-border-margin-bottom) 0',
        borderStyle: 'solid',
        color: 'var(--top-border-color)',
      }}
    ></hr>
  );
};

const serializers = {
  marks: {
    internalLink: InternalLinkSerializer,
    link: LinkSerializer,
  },
  types: {
    button: ButtonSerializer,
    horizontalLine: HorizontalLineSerializer,
  },
};

export default serializers;
